import {DEFAULT_EXPERIMENT_FALLBACK, EXPERIMENT_ON_VALUES} from '../constants/amplitudeExperiment';

export type ExperimentVariant = {
  key: string;
  value: string;
  payload?: unknown;
};

export type ExperimentUser = {
  user_id?: string;
  device_id?: string;
  user_properties?: Record<string, unknown>;
};

export type ExperimentVariants = Record<string, ExperimentVariant>;

function asVariant(raw: AmplitudeExperimentVariant | string | undefined, fallback: string): ExperimentVariant {
  if (typeof raw === 'string' && raw.length > 0) {
    return {key: raw, value: raw};
  }
  if (raw && typeof raw === 'object') {
    const value = raw.value || raw.key || fallback;
    return {
      key: raw.key || value,
      value,
      payload: raw.payload,
    };
  }
  return {key: fallback, value: fallback};
}

export function isAmplitudeExperimentEnabled(): boolean {
  return window.REACT_APP_ENABLE_AMPLITUDE_EXPERIMENT !== false;
}

export function getWebExperimentClient(): AmplitudeWebExperimentClient | null {
  if (!isAmplitudeExperimentEnabled()) {
    return null;
  }
  const client = window.webExperiment;
  if (!client || typeof client !== 'object') {
    return null;
  }
  return client;
}

export function isAmplitudeExperimentReady(): boolean {
  return getWebExperimentClient() !== null;
}

export function getExperimentVariant(
  flagKey: string,
  fallback: string = DEFAULT_EXPERIMENT_FALLBACK
): ExperimentVariant {
  const client = getWebExperimentClient();
  if (!client) {
    return {key: fallback, value: fallback};
  }

  if (typeof client.variant === 'function') {
    return asVariant(client.variant(flagKey, fallback), fallback);
  }

  const allVariants = getAllExperimentVariants();
  if (allVariants[flagKey]) {
    return allVariants[flagKey];
  }

  return {key: fallback, value: fallback};
}

export function getAllExperimentVariants(): ExperimentVariants {
  const client = getWebExperimentClient();
  if (!client) {
    return {};
  }

  const rawVariants =
    (typeof client.all === 'function' && client.all()) ||
    (typeof client.getVariants === 'function' && client.getVariants()) ||
    {};

  return Object.keys(rawVariants).reduce<ExperimentVariants>((variants, flagKey) => {
    variants[flagKey] = asVariant(rawVariants[flagKey], DEFAULT_EXPERIMENT_FALLBACK);
    return variants;
  }, {});
}

export function isExperimentOn(value: string): boolean {
  return (EXPERIMENT_ON_VALUES as readonly string[]).includes(value);
}

export function trackExperimentExposure(flagKey: string): void {
  const client = getWebExperimentClient();
  if (client && typeof client.exposure === 'function') {
    client.exposure(flagKey);
  }
}

export function setExperimentUser(user: ExperimentUser): void {
  const client = getWebExperimentClient();
  if (!client || typeof client.setUser !== 'function') {
    return;
  }
  client.setUser(user);
}

export function reapplyExperimentVariants(): void {
  const client = getWebExperimentClient();
  if (client && typeof client.reapplyVariants === 'function') {
    client.reapplyVariants();
  }
}

type ExperimentInjectAction = {
  action?: string;
  data?: {
    js?: string;
  };
};

function isInjectAction(item: unknown): item is ExperimentInjectAction {
  return Boolean(item && typeof item === 'object' && (item as ExperimentInjectAction).action === 'inject');
}

/**
 * Reads the Amplitude Web Experiment Custom Code JS string from a variant payload.
 * Payload shape: [{action: "mutate", ...}, {action: "inject", data: {js: "..."}}]
 */
export function getExperimentCustomCodeJs(payload: unknown): string | undefined {
  if (!Array.isArray(payload)) {
    return undefined;
  }

  const injectAction = payload.find(isInjectAction);
  const customJs = injectAction?.data?.js;
  if (typeof customJs !== 'string') {
    return undefined;
  }

  const trimmed = customJs.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function quoteUnquotedObjectKeys(source: string): string {
  return source.replace(/([{,]\s*)([A-Za-z_$][\w$]*)\s*:/g, '$1"$2":');
}

/**
 * Parses Custom Code JS into a data object.
 * Supports JSON (`{"type":"treatment"}`) and JS object literals (`{type: "treatment"}`).
 */
export function parseExperimentCustomCodeData(customJs: string | undefined): Record<string, unknown> | undefined {
  if (!customJs) {
    return undefined;
  }

  const candidates = [customJs, quoteUnquotedObjectKeys(customJs)];
  for (const candidate of candidates) {
    try {
      const parsed: unknown = JSON.parse(candidate);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Try the next candidate.
    }
  }

  return undefined;
}

export function getExperimentCustomCodeData(payload: unknown): Record<string, unknown> | undefined {
  return parseExperimentCustomCodeData(getExperimentCustomCodeJs(payload));
}
